import React, { useState } from 'react';
import Modal from '../common/Modal';

const INPUT = 'w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] focus:border-[var(--accent)] transition-all';
const LABEL = 'block text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-1.5';

function Field({ label, error, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

const ClientModal = ({ isOpen, onClose, clients, onSelectClient, onCreateClient }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    passportNumber: '',
    passportExpiry: '',
    passportCountry: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: ''
  });
  const [errors, setErrors] = useState({});

  const filteredClients = clients.filter(client => {
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    const phone = (client.phone || '').toLowerCase();
    const search = searchTerm.toLowerCase();

    return fullName.includes(search) || email.includes(search) || phone.includes(search);
  });

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
    const newErrors = {};

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Format data for API
    const clientData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      date_of_birth: formData.dateOfBirth || null,
      passport_number: formData.passportNumber || null,
      passport_expiry: formData.passportExpiry || null,
      passport_country: formData.passportCountry || null,
      emergency_contact_name: formData.emergencyContactName || null,
      emergency_contact_phone: formData.emergencyContactPhone || null,
      emergency_contact_relation: formData.emergencyContactRelation || null
    };

    await onCreateClient(clientData);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showCreateForm ? 'Create New Client' : 'Select Client'}
      maxWidth="700px"
    >
      <div className="voyager-app">
        {!showCreateForm ? (
          <>
            {/* Client Selection View */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search clients by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${INPUT} pl-10`}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105 active:scale-[0.99]"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                Create New Client
              </button>
            </div>

            <div className="max-h-[400px] overflow-y-auto space-y-2.5">
              {filteredClients.length === 0 ? (
                <p className="text-center text-sm text-[var(--muted-foreground)] py-10">
                  {searchTerm ? 'No clients found matching your search.' : 'No clients found. Create a new client to get started.'}
                </p>
              ) : (
                filteredClients.map(client => (
                  <button
                    type="button"
                    key={client.id}
                    onClick={() => {
                      onSelectClient({
                        ...client,
                        firstName: client.first_name,
                        lastName: client.last_name
                      });
                      onClose();
                    }}
                    className="w-full text-left p-4 rounded-xl border transition-all hover:shadow-sm hover:border-[var(--accent)]/40"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{client.first_name} {client.last_name}</span>
                      <span className="text-xs text-[var(--muted-foreground)]">{client.email}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-[var(--muted-foreground)]">
                      <span>{client.phone}</span>
                      {client.quote_count > 0 && (
                        <span className="px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)' }}>{client.quote_count} quotes</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            {/* Create Client Form */}
            <form onSubmit={handleCreateClient} className="space-y-5">
              <div>
                <h4 className="text-sm font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-3 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>Personal Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="First Name *" error={errors.firstName}>
                    <input type="text" value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className={`${INPUT} ${errors.firstName ? 'border-red-400' : ''}`} />
                  </Field>
                  <Field label="Last Name *" error={errors.lastName}>
                    <input type="text" value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className={`${INPUT} ${errors.lastName ? 'border-red-400' : ''}`} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Email *" error={errors.email}>
                    <input type="email" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`${INPUT} ${errors.email ? 'border-red-400' : ''}`} />
                  </Field>
                  <Field label="Phone *" error={errors.phone}>
                    <input type="tel" value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className={`${INPUT} ${errors.phone ? 'border-red-400' : ''}`} />
                  </Field>
                </div>
                <div className="mb-4">
                  <Field label="Address">
                    <input type="text" value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Street address, city, state, zip" className={INPUT} />
                  </Field>
                </div>
                <Field label="Date of Birth">
                  <input type="date" value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    className={`${INPUT} cursor-pointer`} />
                </Field>
              </div>

              <div>
                <h4 className="text-sm font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-3 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>Travel Documents</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Passport Number">
                    <input type="text" value={formData.passportNumber}
                      onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
                      className={INPUT} />
                  </Field>
                  <Field label="Passport Expiry">
                    <input type="date" value={formData.passportExpiry}
                      onChange={(e) => setFormData({ ...formData, passportExpiry: e.target.value })}
                      className={`${INPUT} cursor-pointer`} />
                  </Field>
                  <Field label="Passport Country">
                    <input type="text" value={formData.passportCountry}
                      onChange={(e) => setFormData({ ...formData, passportCountry: e.target.value })}
                      className={INPUT} />
                  </Field>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-3 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>Emergency Contact</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Contact Name">
                    <input type="text" value={formData.emergencyContactName}
                      onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                      className={INPUT} />
                  </Field>
                  <Field label="Contact Phone">
                    <input type="tel" value={formData.emergencyContactPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                      className={INPUT} />
                  </Field>
                  <Field label="Relationship">
                    <input type="text" value={formData.emergencyContactRelation}
                      onChange={(e) => setFormData({ ...formData, emergencyContactRelation: e.target.value })}
                      className={INPUT} />
                  </Field>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:bg-[var(--muted)]"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  Back to List
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105 active:scale-[0.99]"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  Create Client
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ClientModal;
